/**
 * BINGO ドメイン型定義
 *
 * DDDに基づく値オブジェクト、エンティティ、集約ルートの定義
 */

// ============================================
// ブランド型ユーティリティ
// ============================================

declare const brand: unique symbol;
type Brand<T, B> = T & { readonly [brand]: B };

// ============================================
// 値オブジェクト (Value Objects)
// ============================================

/** ルームID */
export type RoomId = Brand<string, "RoomId">;

/** ルームコード（参加用の短いコード） */
export type RoomCode = Brand<string, "RoomCode">;

/** 参加者ID */
export type ParticipantId = Brand<string, "ParticipantId">;

/** ビンゴ番号 (1-75) */
export type BingoNumber = Brand<number, "BingoNumber">;

/** 玉の数（ゲームモード） */
export type BallCount = 45 | 60 | 75;

/** テーマID */
export type ThemeId = Brand<string, "ThemeId">;

/** チャットメッセージID */
export type ChatMessageId = Brand<string, "ChatMessageId">;

/** ビンゴカード（5x5グリッド、中央はFREE） */
export interface BingoCard {
  readonly id: string;
  /** 25マスの配列（中央インデックス12はFREE） */
  readonly grid: readonly (BingoNumber | "FREE")[];
  /** マークされたセルのインデックス */
  readonly markedIndices: readonly number[];
  /** カードのテーマID */
  readonly themeId: ThemeId;
}

/** 勝利ライン */
export interface WinningLine {
  readonly indices: readonly number[];
  readonly type: "row" | "column" | "diagonal";
}

/** チャットメッセージ */
export interface ChatMessage {
  readonly id: ChatMessageId;
  readonly participantId: ParticipantId;
  readonly nickname: string;
  readonly content: string;
  readonly timestamp: string; // ISO 8601
}

// ============================================
// エンティティ (Entities)
// ============================================

/** 参加者 */
export interface Participant {
  readonly id: ParticipantId;
  readonly nickname: string;
  readonly card: BingoCard;
  readonly joinedAt: string; // ISO 8601
  readonly bingoCount: number;
  readonly isConnected: boolean;
}

/** 主催者 */
export interface Host {
  readonly id: ParticipantId;
  readonly nickname: string;
  readonly isConnected: boolean;
}

// ============================================
// 集約ルート (Aggregate Roots)
// ============================================

/** ルーム状態 */
export type RoomStatus =
  | "created" // 作成直後（主催者のみ）
  | "waiting" // 参加者待機中
  | "playing" // ゲーム進行中
  | "paused" // 一時停止
  | "finished" // ゲーム終了
  | "closed"; // ルーム閉鎖

/** ゲーム集約 */
export interface Game {
  readonly drawnNumbers: readonly BingoNumber[];
  readonly currentNumber: BingoNumber | null;
  readonly winners: readonly ParticipantId[];
  readonly startedAt: string | null; // ISO 8601
  readonly finishedAt: string | null; // ISO 8601
}

/** ルーム設定 */
export interface RoomSettings {
  /** 最大参加者数（デフォルト: 100） */
  readonly maxParticipants: number;
  /** 自動マーク機能 */
  readonly autoMarkEnabled: boolean;
  /** テーマ */
  readonly theme: ThemeId;
  /** ゲーム中の途中参加許可 */
  readonly allowLateJoin: boolean;
  /** 何ビンゴで終了（0=無制限） */
  readonly bingoGoal: number;
  /** 玉の数（45=ショート, 60=ミディアム, 75=スタンダード） */
  readonly ballCount: BallCount;
}

/** ルーム集約（ルート） */
export interface Room {
  readonly id: RoomId;
  readonly code: RoomCode;
  readonly status: RoomStatus;
  readonly host: Host;
  readonly participants: Record<string, Participant>; // ParticipantId -> Participant
  readonly game: Game | null;
  readonly settings: RoomSettings;
  readonly createdAt: string; // ISO 8601
  readonly expiresAt: string; // ISO 8601
}

// ============================================
// イベントログ型
// ============================================

/** イベントログエントリ */
export type GameEventLogEntry =
  | {
      readonly type: "CARD_MARKED";
      readonly timestamp: string;
      readonly participantId: ParticipantId;
      readonly nickname: string;
      readonly number: BingoNumber;
      /** イベント発生時の抽選回数 */
      readonly drawCount: number;
    }
  | {
      readonly type: "REACH_ACHIEVED";
      readonly timestamp: string;
      readonly participantId: ParticipantId;
      readonly nickname: string;
      readonly reachCount: number;
      /** イベント発生時の抽選回数 */
      readonly drawCount: number;
    }
  | {
      readonly type: "BINGO_ACHIEVED";
      readonly timestamp: string;
      readonly participantId: ParticipantId;
      readonly nickname: string;
      readonly bingoCount: number;
      /** イベント発生時の抽選回数 */
      readonly drawCount: number;
    };

// ============================================
// API レスポンス型
// ============================================

/** ルーム作成レスポンス */
export interface CreateRoomResponse {
  readonly roomId: RoomId;
  readonly roomCode: RoomCode;
  readonly hostId: ParticipantId;
  /** 主催者がプレイヤーとして参加する場合のカード */
  readonly hostCard?: BingoCard;
}

/** ルーム参加レスポンス */
export interface JoinRoomResponse {
  readonly participantId: ParticipantId;
  readonly card: BingoCard;
}

/** ルーム参加レスポンス（roomId付き） */
export interface JoinRoomResponseWithRoomId extends JoinRoomResponse {
  readonly roomId: string;
}

/** ルーム情報レスポンス（公開情報のみ） */
export interface RoomInfoResponse {
  readonly roomId: RoomId;
  readonly code: RoomCode;
  readonly status: RoomStatus;
  readonly hostNickname: string;
  readonly participantCount: number;
  readonly maxParticipants: number;
  readonly settings: Pick<RoomSettings, "theme" | "allowLateJoin">;
}

// ============================================
// ヘルパー関数型（ランタイム検証付き）
// ============================================

/** UUID形式のバリデーション正規表現 */
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** ルームコード形式のバリデーション正規表現 */
const ROOM_CODE_REGEX = /^[A-Z0-9]{4}-[A-Z0-9]{4}$/;

/**
 * ルームIDを生成（ランタイム検証付き）
 * Durable Object ID はUUID形式または16進数文字列
 */
export function createRoomId(id: string): RoomId {
  if (typeof id !== "string" || id.length === 0) {
    throw new Error(`Invalid room ID: ${id}. Must be a non-empty string.`);
  }
  return id as RoomId;
}

/**
 * ルームコードを生成（ランタイム検証付き）
 * 形式: XXXX-XXXX（大文字英数字）
 */
export function createRoomCode(code: string): RoomCode {
  const upperCode = code.toUpperCase();
  if (!ROOM_CODE_REGEX.test(upperCode)) {
    throw new Error(`Invalid room code: ${code}. Must match XXXX-XXXX format.`);
  }
  return upperCode as RoomCode;
}

/**
 * 参加者IDを生成（ランタイム検証付き）
 * 形式: UUID
 */
export function createParticipantId(id: string): ParticipantId {
  if (!UUID_REGEX.test(id)) {
    throw new Error(`Invalid participant ID: ${id}. Must be a valid UUID.`);
  }
  return id as ParticipantId;
}

/**
 * ビンゴ番号を生成（ランタイム検証付き）
 * 範囲: 1-75
 */
export function createBingoNumber(n: number): BingoNumber {
  if (!Number.isInteger(n) || n < 1 || n > 75) {
    throw new Error(`Invalid bingo number: ${n}. Must be an integer between 1 and 75.`);
  }
  return n as BingoNumber;
}

/**
 * テーマIDを生成（ランタイム検証付き）
 */
export function createThemeId(id: string): ThemeId {
  if (typeof id !== "string" || id.length === 0) {
    throw new Error(`Invalid theme ID: ${id}. Must be a non-empty string.`);
  }
  return id as ThemeId;
}

/**
 * チャットメッセージIDを生成（ランタイム検証付き）
 */
export function createChatMessageId(id: string): ChatMessageId {
  if (typeof id !== "string" || id.length === 0) {
    throw new Error(`Invalid chat message ID: ${id}. Must be a non-empty string.`);
  }
  return id as ChatMessageId;
}

/** デフォルトルーム設定 */
export const DEFAULT_ROOM_SETTINGS: RoomSettings = {
  maxParticipants: 100,
  autoMarkEnabled: true,
  theme: "neon" as ThemeId,
  allowLateJoin: true,
  bingoGoal: 0,
  ballCount: 75,
};

// ============================================
// 定数
// ============================================

/** 勝利ライン定義（5x5グリッド） */
export const WINNING_LINES: readonly WinningLine[] = [
  // 横5行
  { indices: [0, 1, 2, 3, 4], type: "row" },
  { indices: [5, 6, 7, 8, 9], type: "row" },
  { indices: [10, 11, 12, 13, 14], type: "row" },
  { indices: [15, 16, 17, 18, 19], type: "row" },
  { indices: [20, 21, 22, 23, 24], type: "row" },
  // 縦5列
  { indices: [0, 5, 10, 15, 20], type: "column" },
  { indices: [1, 6, 11, 16, 21], type: "column" },
  { indices: [2, 7, 12, 17, 22], type: "column" },
  { indices: [3, 8, 13, 18, 23], type: "column" },
  { indices: [4, 9, 14, 19, 24], type: "column" },
  // 対角線2本
  { indices: [0, 6, 12, 18, 24], type: "diagonal" },
  { indices: [4, 8, 12, 16, 20], type: "diagonal" },
] as const;

/** FREEセルのインデックス（中央） */
export const FREE_CELL_INDEX = 12;

// ============================================
// 等価性比較関数（Value Object用）
// ============================================

/**
 * RoomIdの等価性を比較
 */
export function roomIdEquals(a: RoomId, b: RoomId): boolean {
  return a === b;
}

/**
 * RoomCodeの等価性を比較
 */
export function roomCodeEquals(a: RoomCode, b: RoomCode): boolean {
  return a === b;
}

/**
 * ParticipantIdの等価性を比較
 */
export function participantIdEquals(a: ParticipantId, b: ParticipantId): boolean {
  return a === b;
}

/**
 * BingoNumberの等価性を比較
 */
export function bingoNumberEquals(a: BingoNumber, b: BingoNumber): boolean {
  return a === b;
}

/**
 * ThemeIdの等価性を比較
 */
export function themeIdEquals(a: ThemeId, b: ThemeId): boolean {
  return a === b;
}

/**
 * ChatMessageIdの等価性を比較
 */
export function chatMessageIdEquals(a: ChatMessageId, b: ChatMessageId): boolean {
  return a === b;
}

/**
 * BingoCardの等価性を比較（グリッド内容で判定）
 */
export function bingoCardEquals(a: BingoCard, b: BingoCard): boolean {
  if (a.id !== b.id) return false;
  if (a.grid.length !== b.grid.length) return false;
  for (let i = 0; i < a.grid.length; i++) {
    if (a.grid[i] !== b.grid[i]) return false;
  }
  return true;
}
